import './App.css';
import { useState } from 'react';

const services = [
  ['01', 'AI strategy & roadmaps', 'Identify high-value opportunities, prioritise use cases and create a practical plan for responsible adoption.'],
  ['02', 'Enterprise AI platforms', 'Select and configure the right AI platform, models and architecture for your organisation.'],
  ['03', 'Governance & security', 'Define policies, permissions and controls that keep AI use secure, compliant and accountable.'],
  ['04', 'Knowledge integration', 'Connect approved documents, data and internal knowledge so AI can provide grounded, useful answers.'],
  ['05', 'Microsoft 365 integration', 'Bring AI into SharePoint, Microsoft 365 and the business systems your people already use.'],
  ['06', 'Agentic AI workflows', 'Automate repetitive processes with intelligent agents that can retrieve, reason and take approved actions.'],
  ['07', 'Custom AI assistants', 'Give departments and teams secure assistants tailored to their knowledge, processes and goals.'],
  ['08', 'Training & optimisation', 'Build staff confidence, improve adoption and continuously optimise performance as your needs evolve.']
];

const outcomes = [
  'Answer questions using approved internal documentation',
  'Search company knowledge in seconds',
  'Guide employees through internal processes',
  'Generate documents using your templates',
  'Automate repetitive administrative tasks',
  'Connect securely with business applications'
];

const applications = [
  {
    sector: 'Industrial & manufacturing',
    number: '01',
    title: 'See issues before they stop production.',
    description: 'Use computer vision, predictive maintenance and real-time operational insight to improve quality, throughput and asset reliability.',
    image: '/images/ai-manufacturing-and-automation.jpg',
    alt: 'Automated production line in a modern manufacturing facility',
    className: 'application-wide'
  },
  {
    sector: 'Knowledge & collaboration',
    number: '02',
    title: 'Give every team instant access to expertise.',
    description: 'Connect people with trusted company knowledge, intelligent document workflows and role-specific AI assistants.',
    image: '/images/ai-knowledge-work-and-collaboration.jpg',
    alt: 'A diverse office team collaborating around a screen',
    className: 'application-tall'
  },
  {
    sector: 'Frontline operations',
    number: '03',
    title: 'Put intelligence in the flow of work.',
    description: 'Support field teams with safer processes, faster diagnostics, quality checks and practical guidance at the point of need.',
    image: '/images/ai-frontline-operations.jpg',
    alt: 'Industrial frontline worker inspecting equipment',
    className: 'application-small'
  },
  {
    sector: 'Global supply chains',
    number: '04',
    title: 'Make complex networks easier to navigate.',
    description: 'Improve forecasting, routing, inventory decisions and document processing across global supply and logistics operations.',
    image: '/images/ai-global-supply-chain.jpg',
    alt: 'Shipping containers representing a global logistics network',
    className: 'application-small'
  }
];

function App() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', website: '' });
  const [formStatus, setFormStatus] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormStatus({ type: 'error', text: 'Please complete all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setFormStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/send-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const contentType = response.headers.get('content-type') || '';
      const rawBody = await response.text();
      let result = {};

      if (contentType.includes('application/json') && rawBody) {
        try { result = JSON.parse(rawBody); } catch { result = {}; }
      }

      if (!response.ok) {
        if (rawBody.trim().startsWith('<!DOCTYPE') || rawBody.trim().startsWith('<html')) {
          throw new Error('API is not running locally. Start the API host or deploy environment before testing form submissions.');
        }
        throw new Error(result.error || 'Unable to send your message right now.');
      }

      setFormStatus({ type: 'success', text: 'Thanks, your message has been sent.' });
      setFormData({ name: '', email: '', message: '', website: '' });
    } catch (error) {
      setFormStatus({ type: 'error', text: error.message || 'Something went wrong while sending your message.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="site">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="navbar">
        <a className="navbar-brand-wrap" href="#top" aria-label="Forture home">
          <span className="logo-mark" aria-hidden="true"><img src="/forture-brand-icon-192.png" alt="" /></span>
          <span className="navbar-brand">Forture</span>
        </a>
        <nav aria-label="Main navigation">
          <ul className="navbar-links">
            <li><a href="#services">Services</a></li>
            <li><a href="#applications">Applications</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </nav>
        <a href="#contact" className="nav-cta">Talk to us <span aria-hidden="true">↗</span></a>
      </header>

      <main id="main-content">
        <section className="hero" id="top">
          <div className="hero-glow" aria-hidden="true"></div>
          <div className="hero-content">
            <p className="eyebrow"><span></span> Enterprise AI consulting</p>
            <h1>Stop asking AI questions. <em>Start putting AI to work.</em></h1>
            <p className="hero-lead">
              <strong>Your employees already use AI. Let&apos;s make it work for your organisation.</strong>
              <span>We help organisations build secure, connected AI that understands their knowledge, systems and goals.</span>
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn btn-primary">Explore what’s possible <span aria-hidden="true">→</span></a>
              <a href="#services" className="btn btn-text">View our services <span aria-hidden="true">↓</span></a>
            </div>
            <div className="hero-proof" aria-label="Our approach">
              <span>Secure by design</span><span>Connected to your knowledge</span><span>Built for real outcomes</span>
            </div>
          </div>

        </section>

        <section className="intro section-pad">
          <div className="container split-intro">
            <div>
              <p className="eyebrow"><span></span> Beyond the licence</p>
              <h2>Turn enterprise AI tools into genuine business capability.</h2>
            </div>
            <div className="intro-copy">
              <p>Without access to the right information, AI can only provide generic answers based on public knowledge. It cannot understand your organisation, your customers, your processes or your goals.</p>
              <p>Forture designs, implements and optimises enterprise AI solutions tailored to the way your business actually works—securely connecting the right knowledge, systems and guardrails.</p>
            </div>
          </div>
        </section>

        <section className="services section-pad" id="services">
          <div className="container">
            <div className="section-heading">
              <div>
                <p className="eyebrow light"><span></span> How we help</p>
                <h2>AI that works for your organisation.</h2>
              </div>
              <p>From strategy to production, we bring together the technology, knowledge and controls needed to make enterprise AI genuinely useful.</p>
            </div>
            <div className="service-grid">
              {services.map(([number, title, description]) => (
                <article className="service-card" key={number}>
                  <div className="service-top"><span>{number}</span><span aria-hidden="true">↗</span></div>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="applications section-pad" id="applications">
          <div className="container">
            <div className="applications-heading">
              <div>
                <p className="eyebrow"><span></span> Across the enterprise</p>
                <h2>One capability. <em>Endless applications.</em></h2>
              </div>
              <p>AI is not confined to one department or use case. From the factory floor to global operations, it can help people see more, decide faster and act with confidence.</p>
            </div>
            <div className="application-grid">
              {applications.map((application) => (
                <article className={`application-card ${application.className}`} key={application.number}>
                  <img src={application.image} alt={application.alt} loading="lazy" decoding="async" />
                  <div className="application-shade" aria-hidden="true"></div>
                  <div className="application-number">{application.number}</div>
                  <div className="application-content">
                    <p>{application.sector}</p>
                    <h3>{application.title}</h3>
                    <span>{application.description}</span>
                  </div>
                </article>
              ))}
            </div>
            <p className="applications-note"><span>AI, applied globally</span> Every organisation has different systems, people and priorities. We design the connections that turn broad AI potential into specific business value.</p>
          </div>
        </section>

        <section className="connected section-pad" id="connected-ai">
          <div className="container connected-grid">
            <div className="connected-visual" aria-hidden="true">
              <div className="data-panel panel-docs"><span>KNOWLEDGE</span><i></i><i></i><i></i></div>
              <div className="data-panel panel-apps"><span>SYSTEMS</span><b>365</b><b>CRM</b><b>ERP</b></div>
              <div className="visual-core"><small>CONNECTED</small><strong>AI</strong><span>Trusted answers</span></div>
              <div className="connection-line line-one"></div>
              <div className="connection-line line-two"></div>
            </div>
            <div className="connected-copy">
              <p className="eyebrow"><span></span> Connected intelligence</p>
              <h2>Connected AI delivers better results.</h2>
              <p>When AI can securely access approved business knowledge, it becomes an extension of your organisation—not just another standalone chatbot.</p>
              <ul className="outcome-list">
                {outcomes.map((outcome) => <li key={outcome}><span aria-hidden="true">✓</span>{outcome}</li>)}
              </ul>
            </div>
          </div>
        </section>

        <section className="workflow section-pad">
          <div className="container workflow-grid">
            <div className="workflow-copy">
              <p className="eyebrow light"><span></span> Agentic AI</p>
              <h2>From AI tools to intelligent workflows.</h2>
              <p>Modern AI is about much more than chat. We design agentic workflows that connect AI with your business systems, data and processes.</p>
              <p>These agents retrieve information, automate routine tasks and coordinate activity across applications—while operating within the governance and security controls defined by your organisation.</p>
              <a href="#contact" className="inline-link">Discuss an AI workflow <span aria-hidden="true">→</span></a>
            </div>
            <div className="workflow-steps" aria-label="Agentic workflow process">
              <div><span>01</span><p><strong>Retrieve</strong><small>Find approved information</small></p></div>
              <div><span>02</span><p><strong>Reason</strong><small>Apply context and rules</small></p></div>
              <div><span>03</span><p><strong>Act</strong><small>Complete approved tasks</small></p></div>
              <div><span>04</span><p><strong>Improve</strong><small>Measure and optimise</small></p></div>
            </div>
          </div>
        </section>

        <section className="value-strip">
          <div className="container value-grid">
            <div><strong>More productive</strong><span>Help people complete work faster</span></div>
            <div><strong>Less manual effort</strong><span>Automate repetitive processes</span></div>
            <div><strong>Measurable value</strong><span>Focus investment on real outcomes</span></div>
          </div>
        </section>

        <section className="contact section-pad" id="contact">
          <div className="container contact-grid">
            <div className="contact-copy">
              <p className="eyebrow"><span></span> Start a conversation</p>
              <h2>Make AI work for your organisation.</h2>
              <p>Tell us where you are in your AI journey. We’ll help you identify a practical next step—whether that means shaping a roadmap, connecting your knowledge or building your first intelligent workflow.</p>
              <a href="#enquiry-form" className="contact-email">Tell us what you want to achieve <span aria-hidden="true">→</span></a>
            </div>
            <form className="contact-form" id="enquiry-form" onSubmit={handleSubmit}>
              <label className="hp-field" htmlFor="website">Website</label>
              <input id="website" className="hp-field" type="text" name="website" value={formData.website} onChange={handleChange} autoComplete="off" tabIndex="-1" />
              <div className="form-field">
                <label htmlFor="name">Name</label>
                <input id="name" type="text" name="name" placeholder="Your name" value={formData.name} onChange={handleChange} autoComplete="name" required />
              </div>
              <div className="form-field">
                <label htmlFor="email">Work email</label>
                <input id="email" type="email" name="email" placeholder="you@company.com" value={formData.email} onChange={handleChange} autoComplete="email" required />
              </div>
              <div className="form-field form-field-wide">
                <label htmlFor="message">How can we help?</label>
                <textarea id="message" name="message" placeholder="Tell us a little about your goals" rows="5" value={formData.message} onChange={handleChange} required></textarea>
              </div>
              {formStatus.text && <p className={`form-status ${formStatus.type}`} role="status">{formStatus.text}</p>}
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Sending…' : 'Send enquiry'} <span aria-hidden="true">→</span></button>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <a className="navbar-brand-wrap" href="#top" aria-label="Forture home"><span className="logo-mark" aria-hidden="true"><img src="/forture-brand-icon-192.png" alt="" /></span><span className="navbar-brand">Forture</span></a>
          <p>Enterprise AI, connected to what matters.</p>
          <p>&copy; {new Date().getFullYear()} Forture. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
